import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { redditPostsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, FileText, Loader2, Trash2, Mic, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const RedditPosts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const res = await redditPostsAPI.getAll();
      setPosts(res.data || []);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) { toast.error('Enter a post title'); return; }
    setCreating(true);
    try {
      const res = await redditPostsAPI.create(newTitle.trim());
      toast.success('Post created');
      setShowCreate(false);
      setNewTitle('');
      navigate(`/reddit-posts/${res.data.id}`);
    } catch { toast.error('Failed to create post'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (e, postId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this post?')) return;
    try {
      await redditPostsAPI.delete(postId);
      toast.success('Post deleted');
      fetchPosts();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reddit-posts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Reddit Posts</h1>
          <p className="text-sm text-zinc-500 mt-1">Create and manage Reddit post ideas with media and copy</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="add-reddit-post-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Post
        </Button>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-16 bg-white border border-zinc-200 rounded-sm">
          <FileText className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 text-sm">No Reddit posts yet</p>
          <p className="text-zinc-400 text-xs mt-1">Click "Add Post" to create your first one</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {posts.map((post) => (
            <div
              key={post.id}
              onClick={() => navigate(`/reddit-posts/${post.id}`)}
              className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300 cursor-pointer transition-all group"
              data-testid={`reddit-post-${post.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-zinc-900 group-hover:text-zinc-700 truncate">{post.title}</h3>
                  <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{post.post_copy || 'No copy yet'}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Image size={12} /> {(post.media || []).length} media
                    </span>
                    <span className="flex items-center gap-1">
                      <Mic size={12} /> {(post.voice_notes || []).length} voice notes
                    </span>
                    <span>{post.created_by_name}</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-500"
                  onClick={(e) => handleDelete(e, post.id)}
                  data-testid={`delete-post-${post.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="create-reddit-post-dialog">
          <DialogHeader>
            <DialogTitle>New Reddit Post</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-zinc-700 mb-2 block">Post Title</label>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter post title..."
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="reddit-post-title-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="create-reddit-post-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedditPosts;
